import type { ChangeEvent } from 'react';
import React, { useEffect, useRef } from 'react';
import type { DeliveryZoneCode } from '@noths/polaris-client-localisation';
import { DropDown, IconInfoCircular } from '@noths/polaris-client-ribbons-design-system';

import { DeliveryOptions } from '../DeliveryOptions/DeliveryOptions';
import { DeliveryOptionSkeleton } from '../DeliveryOptions/DeliveryOptionSkeleton';
import { PhotoPersonalisationMessage } from '../PhotoPersonalisationMessage/PhotoPersonalisationMessage';
import {
  COUNTRY_SELECT_LABEL,
  DELIVERY_NOT_AVAILABLE_TO_COUNTRY_FIELD_ERROR,
  DELIVERY_NOT_AVAILABLE_TO_COUNTRY_MESSAGE,
  TIMEZONE_MESSAGE,
} from './copy';
import type { DeliveryBlockProps } from './types';
import * as styles from './DeliveryBlock.styles';

export const DeliveryBlock = ({
  deliveryOptions,
  deliveryOptionsPlaceholderItems,
  groupedCountries,
  loading,
  onCountryChange,
  partnerName,
  photoUploadRequired,
  selectedCountry,
}: DeliveryBlockProps) => {
  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const [countryCode, deliveryZone] = e.target.value.split('|') as [string, DeliveryZoneCode];

    onCountryChange({
      selectedCountry: countryCode,
      selectedZone: deliveryZone,
    });
  };

  const isDeliveryAvailable = Boolean(deliveryOptionsPlaceholderItems.length);
  const showTimezoneMessage = useRef(false);

  useEffect(() => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    showTimezoneMessage.current = userTimeZone !== 'Europe/London';
  }, []);

  return (
    <section css={styles.deliveryBlock}>
      <header>
        <h2>Delivery info</h2>
        <DropDown
          css={styles.countrySelect}
          disabled={loading}
          error={isDeliveryAvailable ? null : DELIVERY_NOT_AVAILABLE_TO_COUNTRY_FIELD_ERROR}
          id="delivery-block-countries"
          label={COUNTRY_SELECT_LABEL}
          labelHidden
          onChange={handleCountryChange}
          options={groupedCountries}
          value={selectedCountry}
        />
        {showTimezoneMessage.current && (
          <div css={styles.timezoneMessageWrapper}>
            <IconInfoCircular height={16} width={16} />
            <p css={styles.timezoneMessage}>{TIMEZONE_MESSAGE}</p>
          </div>
        )}
      </header>
      {isDeliveryAvailable ? (
        loading ? (
          <>
            {deliveryOptionsPlaceholderItems.map(({ service: { code } }) => (
              <DeliveryOptionSkeleton key={`skeleton-${code}`} />
            ))}
          </>
        ) : (
          <DeliveryOptions
            arrivesBy={{
              date: '2025-12-25',
              name: 'Christmas Day',
            }}
            deliveryOptions={deliveryOptions}
            partnerName={partnerName}
          />
        )
      ) : (
        <p css={styles.deliveryNotAvailableMessage}>{DELIVERY_NOT_AVAILABLE_TO_COUNTRY_MESSAGE}</p>
      )}
      {photoUploadRequired && (
        <>
          <hr />
          <PhotoPersonalisationMessage />
        </>
      )}
    </section>
  );
};
